import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

type PdfTextItem = {
  str: string;
  transform: number[];
};

GlobalWorkerOptions.workerSrc = pdfWorker;

export type Sentence = {
  id: number;
  text: string;
  start: number;
  end: number;
};

const EQUATION_PLACEHOLDER = "An equation is shown in the original document.";
const TABLE_PLACEHOLDER =
  "A row of tabular data appears in the original document.";

function stripCitations(text: string): string {
  return text
    .replace(
      /\[\s*\d+(?:\s*[-,–]\s*\d+)*(?:\s*,\s*\d+(?:\s*[-,–]\s*\d+)*)*\s*\]/g,
      "",
    )
    .replace(
      /\(\s*(?:see\s+|cf\.\s+|e\.g\.,?\s+|i\.e\.,?\s+)?[A-Z][A-Za-z'’-]+(?:\s+(?:et\s+al\.?|and\s+[A-Z][A-Za-z'’-]+))?(?:,\s*(?:19|20)\d{2}[a-z]?)\s*\)/g,
      "",
    )
    .replace(/\s{2,}/g, " ")
    .trim();
}

function isPageFurniture(line: string, allLines: string[]): boolean {
  const trimmed = line.trim();
  if (!trimmed) return true;
  if (/^\d+$/.test(trimmed)) return true;
  if (/^page\s+\d+(\s+of\s+\d+)?$/i.test(trimmed)) return true;
  if (
    trimmed.length < 60 &&
    allLines.filter((candidate) => candidate.trim() === trimmed).length >= 3
  ) {
    return true;
  }
  return false;
}

function replaceEquationOrTable(line: string): string | null {
  const symbols = (line.match(/[=+\-*/^∑∫∂∇≈≠≤≥±·×÷√πθλμσΩ]/g) || []).length;
  const letters = (line.match(/[A-Za-z]/g) || []).length;
  if (symbols >= 3 && symbols > letters / 3 && line.length < 200) {
    return EQUATION_PLACEHOLDER;
  }
  if ((line.match(/\b\d+(?:\.\d+)?\b/g) || []).length >= 4 && letters < 30) {
    return TABLE_PLACEHOLDER;
  }
  return null;
}

function dropReferences(text: string): string {
  const match = text.match(
    /\n\s*(references|bibliography|works\s+cited|literature\s+cited)\s*\n/i,
  );
  if (match && match.index !== undefined) {
    return text.slice(0, match.index).trimEnd();
  }
  return text;
}

export function cleanAcademicText(pageTexts: string[]): string {
  const lines = pageTexts.join("\n").split("\n");
  const kept: string[] = [];
  let lastWasPlaceholder = false;
  for (const raw of lines) {
    const line = raw.replace(/\s+/g, " ").trim();
    if (!line) {
      kept.push("");
      lastWasPlaceholder = false;
      continue;
    }
    if (isPageFurniture(line, lines)) continue;
    const placeholder = replaceEquationOrTable(line);
    if (placeholder) {
      if (!lastWasPlaceholder) kept.push(placeholder);
      lastWasPlaceholder = true;
      continue;
    }
    lastWasPlaceholder = false;
    kept.push(line);
  }
  let joined = kept.join("\n");
  joined = joined.replace(/(\w)-\n(\w)/g, "$1$2");
  joined = joined
    .replace(/\n{2,}/g, "\0PARA\0")
    .replace(/\n+/g, " ")
    .replace(/\u0000PARA\u0000/g, "\n\n");
  joined = stripCitations(joined);
  joined = dropReferences(joined);
  return joined.trim();
}

export function splitSentences(text: string): Sentence[] {
  const sentences: Sentence[] = [];
  const pattern = /[^.!?\n]+[.!?]+(?=\s|$)|[^.!?\n]+(?=\n\n|$)/g;
  let match: RegExpExecArray | null;
  let id = 0;
  while ((match = pattern.exec(text)) !== null) {
    const raw = match[0];
    const lead = raw.match(/^\s*/)?.[0].length ?? 0;
    const start = match.index + lead;
    const trimmed = raw.trim();
    if (trimmed) {
      sentences.push({
        id: id++,
        text: trimmed,
        start,
        end: start + trimmed.length,
      });
    }
  }
  return sentences;
}

export async function parsePdfFile(file: File): Promise<{
  fullText: string;
  sentences: Sentence[];
}> {
  const data = await file.arrayBuffer();
  const pdf = await getDocument({ data }).promise;
  const pageTexts: string[] = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const rows: { y: number; parts: { x: number; str: string }[] }[] = [];
    for (const item of content.items) {
      if (!("str" in item) || !(item as PdfTextItem).str) continue;
      const textItem = item as PdfTextItem;
      const x = textItem.transform[4];
      const y = Math.round(textItem.transform[5]);
      let row = rows.find((candidate) => Math.abs(candidate.y - y) < 2);
      if (!row) {
        row = { y, parts: [] };
        rows.push(row);
      }
      row.parts.push({ x, str: textItem.str });
    }
    rows.sort((a, b) => b.y - a.y);
    const pageText = rows
      .map((row) =>
        row.parts
          .sort((a, b) => a.x - b.x)
          .map((part) => part.str)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim(),
      )
      .filter(Boolean)
      .join("\n");
    pageTexts.push(pageText);
  }
  const fullText = cleanAcademicText(pageTexts);
  const sentences = splitSentences(fullText);
  return { fullText, sentences };
}
